import type { IntlShape } from 'react-intl';
import { resolveCompanyLogoAltDisplayName } from './resolve-company-logo-alt-display-name';

/**
 * Localized logo alt for React components (current page locale via react-intl).
 * Non-empty explicit `alt` wins (call-site override).
 *
 * `id`/`defaultMessage` are inlined directly in each `formatMessage()` call (not hoisted to a
 * shared const) because `formatjs extract`'s static analyzer only recognizes inline object
 * literals — a variable reference is invisible to it and gets silently pruned from
 * i18n/extracted/*.json on the next `npm run extract:i18n`, even though the compiled
 * i18n/index.ts keeps serving the last-known-good string until someone recompiles.
 */
export function formatCompanyLogoAltLabelFromProps(
  intl: IntlShape,
  props: {
    alt?: string;
    initialsSourceName?: string | null;
    companyName?: string | null;
    companyId?: string | null;
  }
): string {
  const { alt, initialsSourceName, companyName, companyId } = props;
  if (alt !== undefined && alt !== '') {
    return alt;
  }
  const displayName = resolveCompanyLogoAltDisplayName(initialsSourceName, companyName, companyId);
  if (displayName) {
    return intl.formatMessage(
      {
        id: 'company.logoAlt',
        defaultMessage: '{company} logo',
        description: 'Accessibility text for company logo image, where {company} is the company name',
      },
      { company: displayName },
    );
  }
  return intl.formatMessage({
    id: 'company.logoAltGeneric',
    defaultMessage: 'Employer logo',
    description: 'Accessibility text when the employer name is unknown',
  });
}
